// frontend/src/pages/Teachers.tsx

import React, { useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  getTeachers,
  createTeacher,
  updateTeacher,
  deleteTeacher,
} from "../api/teachers";
import {
  Teacher,
  CreateTeacherRequest,
  UpdateTeacherRequest,
} from "../types/teacher";
import Sidebar from "../components/Sidebar";
import TeacherForm from "../components/TeacherForm";
import EditTeacherForm from "../components/EditTeacherForm";
import {
  Plus,
  Download,
  Search,
  Eye,
  Edit3,
  Trash2,
  Users,
  Mail,
  X,
} from "lucide-react";
import { generatePaginationItems } from "../utils/pagination";

export default function Teachers() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // — Data & loading —
  const [list, setList] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);

  // — Modal state —
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<Teacher | null>(null);

  // — Filters —
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCourse, setFilterCourse] = useState<"all" | string>("all");

  // — Pagination —
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // 1) Fetch once on mount
  useEffect(() => {
    let mounted = true;

    (async () => {
      const data = await getTeachers();

      if (!mounted) return;

      setList(data);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // 2) Build course filter options
  const courseOptions = useMemo(
    () =>
      Array.from(
        new Set(list.flatMap((t) => t.courses.map((c) => c.name))),
      ).sort(),
    [list],
  );

  // 3) Apply search + course filters
  const filtered = useMemo(() => {
    const q = searchTerm.toLowerCase();

    return list
      .slice()
      .sort(
        (a, b) =>
          new Date(b.joinedDate).getTime() - new Date(a.joinedDate).getTime(),
      )
      .filter((t) => {
        const matchesSearch =
          t.name.toLowerCase().includes(q) ||
          t.email.toLowerCase().includes(q);

        const matchesCourse =
          filterCourse === "all" ||
          t.courses.some((c) => c.name === filterCourse);

        return matchesSearch && matchesCourse;
      });
  }, [list, searchTerm, filterCourse]);

  // 4) Pagination math
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const paginated = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, currentPage, rowsPerPage]);

  // 5) Reset page if out of bounds
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // — Auth guard —
  if (!user) return <Navigate to="/" replace />;

  const canEdit = user.role !== "viewer";
  const canDelete = user.role === "superadmin";

  // — CRUD handlers —
  const handleCreate = async (d: CreateTeacherRequest) => {
    const t = await createTeacher(d);
    setList((l) => [t, ...l]);
    setShowNew(false);
  };

  const handleUpdate = async (id: string, d: UpdateTeacherRequest) => {
    const t = await updateTeacher(id, d);
    setList((l) => l.map((x) => (x.id === id ? t : x)));
    setEditTarget(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this teacher?")) return;

    await deleteTeacher(id);
    setList((l) => l.filter((x) => x.id !== id));
  };

  // — CSV export logic —
  const csvEscape = (v: string | number) => {
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };

  const handleExport = () => {
    const headers = [
      "Name",
      "Email",
      "Salary",
      "JoinedDate",
      "CreatedBy",
      "PAN",
      "Courses",
    ];

    const rows = list.map((t) => [
      t.name,
      t.email,
      t.salary,
      t.joinedDate,
      t.createdBy?.username || t.createdByName || "",
      t.panNumber,
      t.courses.map((c) => c.name).join(";"),
    ]);

    const csv = [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "teachers_export.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterCourse("all");
  };

  const startIndex = (currentPage - 1) * rowsPerPage;

  const fieldClass = `
    box-border h-10 min-w-0 w-full max-w-full rounded-[9px]
    border border-slate-200 bg-white px-3
    text-[12px] font-medium text-slate-800
    outline-none transition
    placeholder:text-slate-400
    hover:border-slate-300
    focus:border-slate-400
    focus:ring-2 focus:ring-slate-100
  `;

  const iconButtonClass = `
    inline-flex h-8 w-8 shrink-0 items-center justify-center
    rounded-lg border bg-white p-0 transition
    focus:outline-none focus:ring-2
  `;

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      <main
        className="
          ml-[280px] min-w-0 px-6 py-6
          transition-[margin] duration-200
          [.sidebar-collapsed_&]:ml-[76px]
          max-lg:ml-[76px]
          max-lg:px-4
          max-sm:px-3
        "
      >
        <div className="mx-auto w-full max-w-[1600px]">
          {/* ============================================================
              HEADER
          ============================================================ */}
          <header className="mb-5 flex items-start justify-between gap-4 max-md:flex-col max-md:items-stretch">
            <div className="flex min-w-0 items-start gap-3">
              <div
                className="
                  grid h-10 w-10 shrink-0 place-items-center
                  rounded-[10px] bg-slate-100 text-slate-600
                "
              >
                <Users size={20} strokeWidth={1.8} />
              </div>

              <div className="min-w-0">
                <div
                  className="
                    mb-1 text-[10px] font-bold uppercase
                    tracking-[0.12em] text-blue-600
                  "
                >
                  Teacher management
                </div>

                <h1
                  className="
                    m-0 text-[28px] font-bold
                    tracking-[-0.03em] text-slate-950
                    max-sm:text-[24px]
                  "
                >
                  Teachers
                </h1>

                <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
                  Manage teachers, assigned courses and payment details.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 max-sm:grid max-sm:grid-cols-2">
              {canEdit && (
                <>
                  <button
                    type="button"
                    onClick={handleExport}
                    className="
                      inline-flex h-10 items-center justify-center gap-2
                      rounded-[9px]
                      border border-emerald-200
                      bg-white px-4
                      text-[11px] font-semibold text-emerald-700
                      transition
                      hover:border-emerald-300
                      hover:bg-emerald-50
                      hover:text-emerald-800
                      focus:outline-none
                      focus:ring-2
                      focus:ring-emerald-200
                    "
                  >
                    <Download size={15} />
                    Export
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowNew(true)}
                    className="
                      inline-flex h-10 items-center justify-center gap-2
                      rounded-[9px]
                      border border-emerald-600
                      bg-emerald-600 px-4
                      text-[11px] font-semibold text-white
                      transition
                      hover:border-emerald-700
                      hover:bg-emerald-700
                      focus:outline-none
                      focus:ring-2
                      focus:ring-emerald-200
                    "
                  >
                    <Plus size={15} />
                    New Teacher
                  </button>
                </>
              )}
            </div>
          </header>

          {/* ============================================================
              FILTERS
          ============================================================ */}
          <section
            className="
              mb-4 rounded-[16px]
              border border-slate-200 bg-white
              p-4
              shadow-[0_1px_3px_rgba(15,23,42,0.04)]
            "
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)]">
              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  Search teachers
                </span>

                <div className="relative">
                  <Search
                    size={16}
                    className="
                      pointer-events-none absolute left-3 top-1/2
                      -translate-y-1/2 text-slate-400
                    "
                  />

                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${fieldClass} pl-9`}
                  />
                </div>
              </label>

              <label className="min-w-0">
                <span
                  className="
                    mb-1.5 block
                    text-[10px] font-semibold uppercase
                    tracking-[0.04em] text-slate-400
                  "
                >
                  Course
                </span>

                <select
                  value={filterCourse}
                  onChange={(e) => setFilterCourse(e.target.value)}
                  className={fieldClass}
                >
                  <option value="all">All Courses</option>

                  {courseOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div
              className="
                mt-4 flex flex-wrap items-center justify-between gap-2
                border-t border-slate-100 pt-3
              "
            >
              <span className="text-[10px] text-slate-400">
                Filters apply to teacher records.
              </span>

              <button
                type="button"
                onClick={clearFilters}
                className="
                  inline-flex h-8 items-center justify-center gap-1.5
                  rounded-lg
                  border border-emerald-200
                  bg-white px-3
                  text-[13px] font-semibold text-emerald-700
                  transition
                  hover:border-emerald-300
                  hover:bg-emerald-50
                  hover:text-emerald-800
                  focus:outline-none
                  focus:ring-2
                  focus:ring-emerald-200
                "
              >
                <X size={13} />
                Clear filters
              </button>
            </div>
          </section>

          {/* ============================================================
              LOADING / TABLE
          ============================================================ */}
          {loading ? (
            <section
              className="
                flex min-h-[260px] flex-col items-center justify-center
                rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <span
                className="
                  h-6 w-6 animate-spin rounded-full
                  border-[3px] border-slate-200 border-t-slate-700
                "
              />

              <span className="mt-3 text-[11px] font-medium text-slate-500">
                Loading teachers…
              </span>
            </section>
          ) : (
            <section
              className="
                overflow-hidden rounded-[16px]
                border border-slate-200 bg-white
                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
              "
            >
              <div
                className="
                  flex items-center justify-between gap-4
                  border-b border-slate-100
                  px-5 py-4
                  max-sm:flex-col max-sm:items-start max-sm:px-4
                "
              >
                <div>
                  <h2 className="m-0 text-[18px] font-bold text-slate-950">
                    Teachers
                  </h2>

                  <p className="mb-0 mt-1 text-[13px] text-slate-500">
                    Showing{" "}
                    <strong className="font-semibold text-slate-600">
                      {filtered.length}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-slate-600">
                      {list.length}
                    </strong>{" "}
                    teacher{list.length === 1 ? "" : "s"}
                  </p>
                </div>

                <div className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-500">
                  <strong className="font-semibold text-slate-800">
                    {paginated.length}
                  </strong>{" "}
                  on this page
                </div>
              </div>

              {paginated.length > 0 ? (
                <>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full min-w-[1080px] border-collapse">
                      <thead className="bg-slate-50/80">
                        <tr>
                          {[
                            "Teacher",
                            "Salary/Fees",
                            "Joined",
                            "PAN",
                            "Courses",
                          ].map((label) => (
                            <th
                              key={label}
                              className="
                                px-4 py-3.5 text-left
                                text-[14px] font-semibold uppercase
                                tracking-[0.04em] text-slate-600
                              "
                            >
                              {label}
                            </th>
                          ))}

                          {canEdit && (
                            <th
                              className="
                                w-[130px] px-4 py-3.5 text-center
                                text-[14px] font-semibold uppercase
                                tracking-[0.04em] text-slate-600
                              "
                            >
                              Actions
                            </th>
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {paginated.map((t) => (
                          <tr
                            key={t.id}
                            className="
                              border-b border-slate-100
                              transition-colors
                              last:border-b-0
                              hover:bg-slate-50/60
                            "
                          >
                            <td className="px-4 py-4 align-middle">
                              <div className="min-w-[230px]">
                                <strong
                                  className="
                                    block max-w-[230px] truncate
                                    text-[15px] font-semibold text-slate-950
                                  "
                                  title={t.name}
                                >
                                  {t.name}
                                </strong>

                                <div
                                  className="
                                    mt-1 flex max-w-[250px]
                                    items-center gap-1.5
                                    text-[13px] text-slate-500
                                  "
                                >
                                  <Mail
                                    size={13}
                                    className="shrink-0 text-slate-400"
                                  />

                                  <span className="truncate" title={t.email}>
                                    {t.email}
                                  </span>
                                </div>
                              </div>
                            </td>

                            <td className="px-4 py-4 align-middle">
                              <div className="min-w-[120px] text-[14px] font-semibold text-slate-900">
                                {"Rs " +
                                  new Intl.NumberFormat("en-NP").format(t.salary)}
                              </div>
                            </td>

                            <td className="px-4 py-4 align-middle">
                              <div className="min-w-[140px]">
                                <div className="text-[14px] font-medium text-slate-700">
                                  {new Date(t.joinedDate).toLocaleDateString()}
                                </div>

                                <div
                                  className="
                                    mt-1 max-w-[170px] truncate
                                    text-[12px] text-slate-500
                                  "
                                  title={
                                    t.createdBy?.username ??
                                    t.createdByName ??
                                    undefined
                                  }
                                >
                                  {t.createdBy?.username ?? t.createdByName ?? "—"}
                                </div>
                              </div>
                            </td>

                            <td className="px-4 py-4 align-middle">
                              <span
                                className="
                                  inline-flex rounded-full
                                  bg-slate-100 px-2.5 py-1
                                  text-[12px] font-semibold text-slate-700
                                "
                              >
                                {t.panNumber || "—"}
                              </span>
                            </td>

                            <td className="px-4 py-4 align-middle">
                              <div className="flex min-w-[220px] max-w-[360px] flex-wrap gap-1.5">
                                {t.courses.length > 0 ? (
                                  t.courses.map((c) => (
                                    <span
                                      key={c.id}
                                      className="
                                        inline-flex max-w-[220px]
                                        rounded-full
                                        bg-slate-100 px-2.5 py-1
                                        text-[12px] font-semibold text-slate-700
                                      "
                                      title={c.name}
                                    >
                                      <span className="truncate">{c.name}</span>
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-[13px] text-slate-400">
                                    —
                                  </span>
                                )}
                              </div>
                            </td>

                            {canEdit && (
                              <td className="px-4 py-4 text-center align-middle">
                                <div className="inline-flex items-center justify-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/teachers/${t.id}/logs`)}
                                    title="View Teacher"
                                    aria-label={`View ${t.name}`}
                                    className={`
                                      ${iconButtonClass}
                                      border-slate-200
                                      text-slate-500
                                      hover:border-slate-300
                                      hover:bg-slate-50
                                      hover:text-slate-900
                                      focus:ring-slate-200
                                    `}
                                  >
                                    <Eye size={15} />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => setEditTarget(t)}
                                    title="Edit Teacher"
                                    aria-label={`Edit ${t.name}`}
                                    className={`
                                      ${iconButtonClass}
                                      border-emerald-200
                                      text-emerald-700
                                      hover:border-emerald-300
                                      hover:bg-emerald-50
                                      hover:text-emerald-800
                                      focus:ring-emerald-200
                                    `}
                                  >
                                    <Edit3 size={15} />
                                  </button>

                                  {canDelete && (
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(t.id)}
                                      title="Delete Teacher"
                                      aria-label={`Delete ${t.name}`}
                                      className={`
                                        ${iconButtonClass}
                                        border-red-200
                                        text-red-600
                                        hover:border-red-300
                                        hover:bg-red-50
                                        hover:text-red-700
                                        focus:ring-red-200
                                      `}
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="
                      flex items-center justify-between gap-4
                      border-t border-slate-100
                      px-5 py-3
                      max-sm:flex-col max-sm:items-stretch max-sm:px-4
                    "
                  >
                    <div className="flex flex-wrap items-center gap-2 text-[13px] text-slate-600">
                      <span>Rows per page</span>

                      <select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="
                          h-8 rounded-lg
                          border border-slate-200 bg-white
                          px-2
                          text-[13px] font-medium text-slate-700
                          outline-none
                          focus:border-slate-400
                          focus:ring-2
                          focus:ring-slate-100
                        "
                      >
                        {[5, 10, 20, 50].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>

                      <span>
                        {startIndex + 1}–
                        {Math.min(startIndex + rowsPerPage, filtered.length)} of{" "}
                        {filtered.length}
                      </span>
                    </div>

                    {filtered.length > rowsPerPage && (
                      <div className="flex flex-wrap items-center justify-end gap-1.5 max-sm:justify-start">
                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((p) => Math.max(p - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className="
                            h-8 rounded-lg
                            border border-emerald-200
                            bg-white px-3
                            text-[13px] font-semibold text-emerald-700
                            transition
                            hover:border-emerald-300
                            hover:bg-emerald-50
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                          "
                        >
                          Prev
                        </button>

                        {generatePaginationItems(currentPage, totalPages).map(
                          (item, index) =>
                            item.type === "ellipsis" ? (
                              <span
                                key={`ellipsis-${index}`}
                                className="
                                  inline-flex h-8 min-w-8
                                  items-center justify-center
                                  text-[13px] font-semibold text-slate-500
                                "
                              >
                                …
                              </span>
                            ) : (
                              <button
                                type="button"
                                key={item.page}
                                onClick={() => setCurrentPage(item.page!)}
                                className={`
                                  h-8 min-w-8 rounded-lg border px-2
                                  text-[13px] font-semibold transition
                                  ${
                                    item.isActive
                                      ? `
                                          border-emerald-600
                                          bg-emerald-600
                                          text-white
                                        `
                                      : `
                                          border-emerald-200
                                          bg-white
                                          text-emerald-700
                                          hover:border-emerald-300
                                          hover:bg-emerald-50
                                        `
                                  }
                                `}
                              >
                                {item.page}
                              </button>
                            ),
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((p) =>
                              Math.min(p + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                          className="
                            h-8 rounded-lg
                            border border-emerald-200
                            bg-white px-3
                            text-[13px] font-semibold text-emerald-700
                            transition
                            hover:border-emerald-300
                            hover:bg-emerald-50
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                          "
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div
                  className="
                    flex min-h-[230px] flex-col
                    items-center justify-center px-6 text-center
                  "
                >
                  <div
                    className="
                      grid h-11 w-11 place-items-center
                      rounded-xl bg-slate-100 text-slate-400
                    "
                  >
                    <Users size={20} />
                  </div>

                  <h3 className="mb-0 mt-3 text-[13px] font-semibold text-slate-900">
                    No teachers found
                  </h3>

                  <p
                    className="
                      mb-0 mt-1 max-w-[430px]
                      text-[10px] leading-5 text-slate-500
                    "
                  >
                    Try changing your search or filters to see more teachers.
                  </p>
                </div>
              )}
            </section>
          )}

          {/* ============================================================
              MODALS
          ============================================================ */}
          {showNew && (
            <TeacherForm
              onSubmit={handleCreate}
              onCancel={() => setShowNew(false)}
            />
          )}

          {editTarget && (
            <EditTeacherForm
              teacher={editTarget}
              onSubmit={handleUpdate}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </div>
      </main>
    </div>
  );
}
